import { isNode } from '@pn/core/utils/env';
import { isNil } from 'lodash-es';

type WebStorageService = {
  get: <T>(key: string) => T | undefined;
  set: (key: string, value: unknown) => void;
  unset: (key: string) => void;
};

/**
 * @usage
 * ```ts
 * // In your app:
 * export const ls = webStorageService('app-name');
 *
 * ls.set('example-key', 'some-value');
 * const value = ls.get<string>('example-key'); // not type-safe (add your own type guards if needed)
 *
 * // In non-app packages:
 * webStorageService('example').set('example-key', 'some-value');
 * const value = webStorageService('example').get<string>('example-key');
 *
 * // Scoped to the browser tab/session instead of persisted:
 * webStorageService('example', 'session').set('example-key', 'some-value');
 * ```
 */
export const webStorageService = (
  namespace?: string,
  type: 'local' | 'session' = 'local'
): WebStorageService => ({
  get: <T>(key: string) => {
    if (isNode()) return undefined;

    const item = getStorage(type).getItem(getStorageKey(namespace, key));
    if (isNil(item)) return undefined;
    try {
      // TODO: use zod to validate
      return JSON.parse(item) as T;
    } catch (error) {
      console.log('Error parsing JSON', error);
      return undefined;
    }
  },
  set: (key: string, value: unknown) => {
    if (isNode()) return;
    getStorage(type).setItem(
      getStorageKey(namespace, key),
      JSON.stringify(value)
    );
  },
  unset: (key: string) => {
    if (isNode()) return;
    getStorage(type).removeItem(getStorageKey(namespace, key));
  },
});

function getStorage(type: 'local' | 'session'): Storage {
  return type === 'session' ? sessionStorage : localStorage;
}

function getStorageKey(namespace: string | undefined, key: string): string {
  return isNil(namespace) ? key : `${namespace}-${key}`;
}
